import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl
} from '@mui/material';

const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];

const initForms = {
  r1: { planet1: 'Sun', longitude: '', value: '2' },
  r2: { planet1: 'Sun', planet2: 'Moon', value: '2' },
  r3: { planet1: 'Sun', planet2: 'Moon', value: '2' },
  r4: { planet1: 'Sun', mode: 'rising', value: '2' },
  r5: { planet1: 'Sun', mode: 'rising', value: '2' },
};

const PlanetSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 110 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
PlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const AddValueField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }} sx={{ width: 56 }} />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button variant="contained" size="small" onClick={onClick} disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

const LonField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => {
      const v = parseFloat(e.target.value);
      if (e.target.value === '') { onChange(''); return; }
      if (!isNaN(v)) onChange(String(Math.min(359.99, Math.max(0, v))));
    }}
    inputProps={{ min: 0, max: 359.99, step: 0.01 }} sx={{ width: 90 }} />
);
LonField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const condRowSx = {
  border: '1px solid #d0d0d0', borderRadius: 1, p: 1.5,
  backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
};

const MOAGroup7BTab = ({ onAddCondition, forms: propForms, onFormsChange, natal }) => {
  const [localForms, setLocalForms] = useState(initForms);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;

  const natalReady = !!(natal && natal.date && natal.time && natal.longitude !== '' && natal.latitude !== '');

  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 7, type, ...condObj, natal: natal ? { ...natal } : {}, value: val });
  };

  const gatedSx = natalReady ? {} : { opacity: 0.45, pointerEvents: 'none' };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 1, letterSpacing: 0.5 }}>
        Natal Degree Aspect Studies
      </Typography>

      {!natalReady && (
        <Typography sx={{ textAlign: 'center', color: '#c62828', fontSize: '0.95rem', mb: 2 }}>
          Please set natal data on the <strong>Origin</strong> tab first.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...gatedSx }}>

        {/* Row 1: Planet Conjunct longitude */}
        <Box sx={condRowSx}>
          <Label>Planet</Label>
          <PlanetSelect value={forms.r1.planet1} onChange={v => set('r1', 'planet1', v)} />
          <Label>Conjunct longitude</Label>
          <LonField value={forms.r1.longitude} onChange={v => set('r1', 'longitude', v)} />
          <Box sx={{ flex: 1 }} />
          <Label>Add value</Label>
          <AddValueField value={forms.r1.value} onChange={v => set('r1', 'value', v)} />
          <AddBtn disabled={!forms.r1.longitude} onClick={() => add('g7_conjunct_longitude', { planet1: forms.r1.planet1, longitude: Number(forms.r1.longitude), value: forms.r1.value })} />
        </Box>

        {/* Row 2: Planet Conjunct natal Planet */}
        <Box sx={condRowSx}>
          <Label>Planet</Label>
          <PlanetSelect value={forms.r2.planet1} onChange={v => set('r2', 'planet1', v)} />
          <Label>Conjunct natal Planet</Label>
          <PlanetSelect value={forms.r2.planet2} onChange={v => set('r2', 'planet2', v)} />
          <Box sx={{ flex: 1 }} />
          <Label>Add value</Label>
          <AddValueField value={forms.r2.value} onChange={v => set('r2', 'value', v)} />
          <AddBtn onClick={() => add('g7_conjunct_natal', { planet1: forms.r2.planet1, planet2: forms.r2.planet2, value: forms.r2.value })} />
        </Box>

        {/* Row 3: Planet Opposite natal Planet */}
        <Box sx={condRowSx}>
          <Label>Planet</Label>
          <PlanetSelect value={forms.r3.planet1} onChange={v => set('r3', 'planet1', v)} />
          <Label>Opposite natal Planet</Label>
          <PlanetSelect value={forms.r3.planet2} onChange={v => set('r3', 'planet2', v)} />
          <Box sx={{ flex: 1 }} />
          <Label>Add value</Label>
          <AddValueField value={forms.r3.value} onChange={v => set('r3', 'value', v)} />
          <AddBtn onClick={() => add('g7_opposite_natal_planet', { planet1: forms.r3.planet1, planet2: forms.r3.planet2, value: forms.r3.value })} />
        </Box>

        {/* Row 4: Planet conjunct Rising/Midheaven Longitude */}
        <Box sx={condRowSx}>
          <Label>Planet</Label>
          <PlanetSelect value={forms.r4.planet1} onChange={v => set('r4', 'planet1', v)} />
          <Label>conjunct</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <input type="radio" checked={forms.r4.mode === 'rising'} onChange={() => set('r4', 'mode', 'rising')} />
            <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>Rising Longitude</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <input type="radio" checked={forms.r4.mode === 'midheaven'} onChange={() => set('r4', 'mode', 'midheaven')} />
            <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>Midheaven Longitude</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Label>Add value</Label>
          <AddValueField value={forms.r4.value} onChange={v => set('r4', 'value', v)} />
          <AddBtn onClick={() => add(
            forms.r4.mode === 'rising' ? 'g7_conjunct_rising_longitude' : 'g7_conjunct_midheaven_longitude',
            { planet1: forms.r4.planet1, mode: forms.r4.mode, value: forms.r4.value }
          )} />
        </Box>

        {/* Row 5: Planet in Rising/Midheaven Sign */}
        <Box sx={condRowSx}>
          <Label>Planet</Label>
          <PlanetSelect value={forms.r5.planet1} onChange={v => set('r5', 'planet1', v)} />
          <Label>in</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <input type="radio" checked={forms.r5.mode === 'rising'} onChange={() => set('r5', 'mode', 'rising')} />
            <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>Rising Sign</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <input type="radio" checked={forms.r5.mode === 'midheaven'} onChange={() => set('r5', 'mode', 'midheaven')} />
            <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>Midheaven Sign</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Label>Add value</Label>
          <AddValueField value={forms.r5.value} onChange={v => set('r5', 'value', v)} />
          <AddBtn onClick={() => add(
            forms.r5.mode === 'rising' ? 'g7_in_rising_sign' : 'g7_in_midheaven_sign',
            { planet1: forms.r5.planet1, mode: forms.r5.mode, value: forms.r5.value }
          )} />
        </Box>

      </Box>
    </Box>
  );
};

MOAGroup7BTab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
  natal: PropTypes.object,
};

export default MOAGroup7BTab;
